import { Link } from "react-router-dom";
import { ArrowRight, BookOpen } from "lucide-react";
import { AUTHOR } from "@/data/author";
import { CHECKED_ON } from "@/data/equality";

export const linkClass = "font-semibold underline decoration-pink decoration-2 underline-offset-4";

export function Hero({ eyebrow, title, accent, intro, testid }) {
  return (
    <section data-testid={testid} className="grain relative overflow-hidden bg-ink pb-16 pt-32 sm:pt-40 text-paper">
      <div className="container-x">
        <p className="eyebrow text-pink">{eyebrow}</p>
        <h1 className="mt-5 max-w-4xl font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.04] tracking-tight">
          {title} {accent && <span className="text-pink">{accent}</span>}
        </h1>
        {intro && <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-paper/70">{intro}</p>}
        <p className="mt-5 font-mono text-xs uppercase tracking-[0.18em] text-paper/45">
          {AUTHOR.name}, {AUTHOR.credential} · Law checked {CHECKED_ON.label}
        </p>
      </div>
    </section>
  );
}

export function GuideList({ guides, testid }) {
  return (
    <ul data-testid={testid} className="grid gap-4 md:grid-cols-2">
      {guides.map((g) =>
        g.path ? (
          <li key={g.title}>
            <Link to={g.path} className="group block h-full border-2 border-ink bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[6px_6px_0_0_#F472B6]">
              <BookOpen size={20} className="text-pink" />
              <p className="mt-4 font-display text-xl font-semibold tracking-tight">{g.title}</p>
              <p className="mt-2 text-sm leading-relaxed text-ink/70">{g.blurb}</p>
              <span className="mt-4 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-ink/60 group-hover:text-ink">
                Read <ArrowRight size={14} />
              </span>
            </Link>
          </li>
        ) : (
          <li key={g.title} className="h-full border-2 border-dashed border-ink/25 bg-white/60 p-6">
            <BookOpen size={20} className="text-ink/30" />
            <p className="mt-4 font-display text-xl font-semibold tracking-tight text-ink/70">{g.title}</p>
            <p className="mt-2 text-sm leading-relaxed text-ink/55">{g.blurb}</p>
            <span className="mt-4 inline-block font-mono text-xs uppercase tracking-[0.18em] text-ink/40">Being written</span>
          </li>
        ),
      )}
    </ul>
  );
}

export function CheckerCard({ to, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group block border-2 border-ink bg-ink p-7 text-paper transition-all duration-300 hover:-translate-y-1 hover:shadow-[6px_6px_0_0_#F472B6]"
    >
      <p className="eyebrow text-pink">Start here</p>
      <p className="mt-4 font-display text-2xl sm:text-3xl font-semibold tracking-tight">Is it protected?</p>
      <p className="mt-3 max-w-xl text-sm sm:text-base leading-relaxed text-paper/70">
        Mental health, menopause, neurodivergence, childcare, beliefs: what the Equality Act covers, what it doesn't, and the test that decides it.
      </p>
      <span className="mt-5 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-pink">
        Open the checker <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
      </span>
    </Link>
  );
}

export const breadcrumb = (origin, items) => ({
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({ "@type": "ListItem", position: i + 1, name: it.name, item: `${origin}${it.path}` })),
});
